import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Separator } from "../../components/ui/separator";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../components/ui/dialog";
import { toast } from "sonner";
import { 
  User,
  CreditCard,
  Settings2,
  Save,
  Check,
  ArrowRight,
  Mail,
  Link,
  Unlink,
  ExternalLink,
  AlertCircle,
  CheckCircle2,
  Loader2,
  Users,
  Trash2,
  UserPlus
} from "lucide-react";
import { apiFetch } from "../../lib/apiFetch";
import { useAuth, API } from "../../App";

// Subscription Tab translations
const subscriptionTranslations = {
  fr: {
    title: "Abonnement",
    cardTitle: "Votre plan ALIA",
    infoTitle: "Gérez votre abonnement",
    infoText1: "Votre abonnement ALIA avec 14 jours d'essai gratuit, sans carte bancaire.",
    infoText2: "Paiement par carte bancaire ou SEPA IBAN.",
    infoText3: "Annulez à tout moment sans engagement.",
    modifyTitle: "Modifier votre abonnement",
    modifyText: "Pour changer de plan ou modifier votre abonnement, accédez à la page tarification.",
    ctaQuote: "Voir les tarifs",
    benefits: "Avantages inclus",
    benefit1: "14 jours d'essai gratuit",
    benefit1Desc: "Testez toutes les fonctionnalités",
    benefit2: "Paiement flexible",
    benefit2Desc: "Carte bancaire ou SEPA IBAN",
    benefit3: "Support prioritaire",
    benefit3Desc: "Réponse sous 24h",
    benefit4: "Sans engagement",
    benefit4Desc: "Annulez à tout moment",
    badges: {
      noCard: "Carte ou IBAN accepté",
      billing: "Facturation mensuelle"
    }
  },
  en: {
    title: "Subscription",
    cardTitle: "Your ALIA plan",
    infoTitle: "Manage your subscription",
    infoText1: "Your ALIA subscription with 14-day free trial, no credit card required.",
    infoText2: "Payment via credit card or SEPA IBAN.",
    infoText3: "Cancel anytime, no credit card required.",
    modifyTitle: "Modify your subscription",
    modifyText: "To change plans or modify your subscription, visit the pricing page.",
    ctaQuote: "View pricing",
    benefits: "Included benefits",
    benefit1: "14-day free trial",
    benefit1Desc: "Test all features",
    benefit2: "Flexible payment",
    benefit2Desc: "Credit card or SEPA IBAN",
    benefit3: "Priority support",
    benefit3Desc: "Response within 24h",
    benefit4: "No credit card required",
    benefit4Desc: "Cancel anytime",
    badges: {
      noCard: "Card or IBAN accepted",
      billing: "Monthly billing"
    }
  }
};

// Bilingual Subscription Tab Component
const SubscriptionTab = () => {
  const [lang, setLang] = useState(() => {
    // Try to detect from localStorage or browser
    const saved = localStorage.getItem("alia-lang");
    if (saved) return saved;
    const browserLang = navigator.language?.toLowerCase();
    return browserLang?.startsWith("fr") ? "fr" : "en";
  });

  const t = subscriptionTranslations[lang];

  const toggleLang = (newLang) => {
    setLang(newLang);
    localStorage.setItem("alia-lang", newLang);
  };

  // Fetch current subscription status
  const [subInfo, setSubInfo] = useState(null);
  const [loadingSub, setLoadingSub] = useState(true);
  const [confirmCancelOpen, setConfirmCancelOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const refreshSub = useCallback(async () => {
    setLoadingSub(true);
    try {
      const r = await apiFetch(`${API}/billing/subscription-status`);
      if (r.ok) {
        const d = await r.json();
        setSubInfo(d);
      }
    } catch (_) { /* silent */ } finally { setLoadingSub(false); }
  }, []);

  useEffect(() => { refreshSub(); }, [refreshSub]);

  const daysRemaining = (iso) => {
    if (!iso) return null;
    try {
      const end = new Date(iso).getTime();
      const now = Date.now();
      const diff = Math.ceil((end - now) / (24 * 3600 * 1000));
      return diff > 0 ? diff : 0;
    } catch { return null; }
  };

  const formatDate = (iso) => {
    if (!iso) return "";
    try {
      return new Date(iso).toLocaleDateString(lang === "fr" ? "fr-FR" : "en-US", {
        day: "2-digit", month: "long", year: "numeric"
      });
    } catch { return iso; }
  };

  const handleCancel = async () => {
    setCancelling(true);
    try {
      const r = await apiFetch(`${API}/billing/cancel-subscription`, { method: "POST" });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) {
        toast.error(d.detail || (lang === "fr" ? "Erreur lors de l'annulation" : "Cancellation failed"));
        return;
      }
      toast.success(
        lang === "fr"
          ? `Votre abonnement prendra fin le ${formatDate(d.current_period_end)}`
          : `Your subscription will end on ${formatDate(d.current_period_end)}`
      );
      setConfirmCancelOpen(false);
      refreshSub();
    } catch (e) {
      toast.error(e.message || "Erreur réseau");
    } finally {
      setCancelling(false);
    }
  };

  const isTrialing = subInfo?.subscription_status === "trialing";
  const isActive = subInfo?.subscription_status === "active";
  const isCancelling = !!subInfo?.cancel_at_period_end;
  const canShowCancelButton = !!subInfo?.has_active_stripe_subscription && !isCancelling;
  const trialDays = daysRemaining(subInfo?.trial_ends_at);

  return (
    <Card className="glass-card">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-blue-500" />
            {t.cardTitle}
          </CardTitle>
          {/* Language Toggle */}
          <div className="flex items-center border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
            <button
              onClick={() => toggleLang("fr")}
              className={`px-2.5 py-1 text-xs font-medium transition-colors ${
                lang === "fr" 
                  ? "bg-blue-600 text-white" 
                  : "text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
              }`}
            >
              FR
            </button>
            <button
              onClick={() => toggleLang("en")}
              className={`px-2.5 py-1 text-xs font-medium transition-colors ${
                lang === "en" 
                  ? "bg-blue-600 text-white" 
                  : "text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
              }`}
            >
              EN
            </button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Live subscription status banner */}
        {!loadingSub && subInfo && (
          <>
            {isTrialing && trialDays !== null && (
              <div
                className="p-4 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800"
                data-testid="trial-banner"
              >
                <p className="text-sm font-semibold text-amber-800 dark:text-amber-300" data-testid="trial-days-remaining">
                  {lang === "fr"
                    ? `Essai en cours — ${trialDays} jour${trialDays > 1 ? "s" : ""} restant${trialDays > 1 ? "s" : ""}`
                    : `Trial in progress — ${trialDays} day${trialDays > 1 ? "s" : ""} remaining`}
                </p>
                <p className="text-xs text-amber-700 dark:text-amber-400 mt-1">
                  {lang === "fr"
                    ? `Votre essai se poursuit jusqu'au ${formatDate(subInfo.trial_ends_at)}, sans redémarrer une nouvelle période d'essai.`
                    : `Your trial continues until ${formatDate(subInfo.trial_ends_at)} and will not restart when you change plan.`}
                </p>
              </div>
            )}
            {isCancelling && subInfo.current_period_end && (
              <div
                className="p-4 rounded-xl bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-800"
                data-testid="cancel-pending-banner"
              >
                <p className="text-sm font-semibold text-rose-800 dark:text-rose-300">
                  {lang === "fr"
                    ? `Votre abonnement prendra fin le ${formatDate(subInfo.current_period_end)}.`
                    : `Your subscription will end on ${formatDate(subInfo.current_period_end)}.`}
                </p>
              </div>
            )}
          </>
        )}

        {/* Current Subscription Info */}
        <div className="p-6 rounded-xl bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-blue-600 flex items-center justify-center flex-shrink-0">
              <CheckCircle2 className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-3">
                {t.infoTitle}
              </h3>
              <div className="space-y-1 mb-4">
                <p className="text-slate-600 dark:text-slate-400 text-sm">{t.infoText1}</p>
                <p className="text-slate-600 dark:text-slate-400 text-sm">{t.infoText2}</p>
                <p className="text-slate-600 dark:text-slate-400 text-sm font-medium">{t.infoText3}</p>
              </div>
              <div className="flex flex-wrap gap-3">
                <Badge className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 border-0">
                  {t.badges.noCard}
                </Badge>
                <Badge className="bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 border-0">
                  {t.badges.billing}
                </Badge>
              </div>
            </div>
          </div>
        </div>

        <Separator />

        {/* Billing Contact */}
        <div className="space-y-4">
          <h4 className="font-medium text-slate-900 dark:text-white">
            {t.modifyTitle}
          </h4>
          <p className="text-sm text-slate-600 dark:text-slate-400">
            {t.modifyText}
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4">
            <a href="/pricing" className="flex-1" data-testid="subscription-quote-btn">
              <Button className="w-full bg-blue-600 hover:bg-blue-700 text-white">
                <Mail className="w-4 h-4 mr-2" />
                {t.ctaQuote}
              </Button>
            </a>
            <a href="mailto:contact@alia-assistant.com" className="flex-1">
              <Button variant="outline" className="w-full">
                <Mail className="w-4 h-4 mr-2" />
                contact@alia-assistant.com
              </Button>
            </a>
          </div>
        </div>

        <Separator />

        {/* Enterprise Benefits */}
        <div className="space-y-4">
          <h4 className="font-medium text-slate-900 dark:text-white">
            {t.benefits}
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-start gap-3">
              <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-slate-900 dark:text-white">{t.benefit1}</p>
                <p className="text-sm text-slate-500">{t.benefit1Desc}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-slate-900 dark:text-white">{t.benefit2}</p>
                <p className="text-sm text-slate-500">{t.benefit2Desc}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-slate-900 dark:text-white">{t.benefit3}</p>
                <p className="text-sm text-slate-500">{t.benefit3Desc}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-slate-900 dark:text-white">{t.benefit4}</p>
                <p className="text-sm text-slate-500">{t.benefit4Desc}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Cancel Subscription — only when a real Stripe subscription is active */}
        {canShowCancelButton && (
          <>
            <Separator />
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div>
                <h4 className="font-medium text-slate-900 dark:text-white">
                  {lang === "fr" ? "Annuler mon abonnement" : "Cancel my subscription"}
                </h4>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                  {lang === "fr"
                    ? "Votre abonnement restera actif jusqu'à la fin de la période en cours."
                    : "Your subscription remains active until the end of the current billing period."}
                </p>
              </div>
              <Button
                variant="outline"
                className="border-rose-300 text-rose-600 hover:bg-rose-50 dark:border-rose-800 dark:text-rose-400 dark:hover:bg-rose-900/20"
                onClick={() => setConfirmCancelOpen(true)}
                data-testid="cancel-subscription-btn"
              >
                {lang === "fr" ? "Annuler mon abonnement" : "Cancel my subscription"}
              </Button>
            </div>
          </>
        )}
      </CardContent>

      {/* Confirmation dialog */}
      <Dialog open={confirmCancelOpen} onOpenChange={(o) => !cancelling && setConfirmCancelOpen(o)}>
        <DialogContent data-testid="cancel-confirm-dialog">
          <DialogHeader>
            <DialogTitle>
              {lang === "fr" ? "Confirmer l'annulation" : "Confirm cancellation"}
            </DialogTitle>
            <DialogDescription>
              {lang === "fr"
                ? "Votre abonnement restera actif jusqu'à la fin de votre période de facturation en cours. Voulez-vous confirmer l'annulation ?"
                : "Your subscription will remain active until the end of your current billing period. Do you want to confirm the cancellation?"}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              variant="outline"
              onClick={() => setConfirmCancelOpen(false)}
              disabled={cancelling}
              data-testid="cancel-confirm-abort"
            >
              {lang === "fr" ? "Retour" : "Back"}
            </Button>
            <Button
              className="bg-rose-600 hover:bg-rose-700 text-white"
              onClick={handleCancel}
              disabled={cancelling}
              data-testid="cancel-confirm-submit"
            >
              {cancelling
                ? (lang === "fr" ? "Annulation…" : "Cancelling…")
                : (lang === "fr" ? "Confirmer l'annulation" : "Confirm cancellation")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

const SettingsPage = () => {
  const { user, setUser } = useAuth();
  const [searchParams] = useSearchParams();
  const [saving, setSaving] = useState(false);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [profile, setProfile] = useState({
    name: user?.name || "",
    company_name: user?.company_name || "",
    automation_level: user?.automation_level || "suggestions",
    tone_preference: user?.tone_preference || "formal"
  });

  // Outlook connection state
  const [outlookStatus, setOutlookStatus] = useState({
    loading: true,
    configured: false,
    connected: false,
    email: null,
    displayName: null,
    tokenExpired: false
  });
  const [connectingOutlook, setConnectingOutlook] = useState(false);
  const [disconnectingOutlook, setDisconnectingOutlook] = useState(false);

  // Colleagues state (for delegation dropdown)
  const [colleagues, setColleagues] = useState([]);
  const [colleaguesLoading, setColleaguesLoading] = useState(false);
  const [newColleagueName, setNewColleagueName] = useState("");
  const [newColleagueEmail, setNewColleagueEmail] = useState("");
  const [addingColleague, setAddingColleague] = useState(false);

  const loadColleagues = useCallback(async () => {
    setColleaguesLoading(true);
    try {
      const r = await apiFetch(`${API}/users/me/colleagues`);
      if (r.ok) {
        const d = await r.json();
        setColleagues(d.colleagues || []);
      }
    } catch (_) { /* silent */ }
    finally { setColleaguesLoading(false); }
  }, []);

  useEffect(() => { loadColleagues(); }, [loadColleagues]);

  const handleAddColleague = async (e) => {
    e?.preventDefault?.();
    const name = newColleagueName.trim();
    const email = newColleagueEmail.trim();
    if (!name) { toast.error("Nom requis"); return; }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) { toast.error("Adresse e-mail invalide"); return; }
    setAddingColleague(true);
    try {
      const r = await apiFetch(`${API}/users/me/colleagues`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, email })
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) {
        toast.error(d.detail || "Erreur lors de l'ajout");
        return;
      }
      setColleagues((prev) => [...prev, d].sort((a, b) => a.name.localeCompare(b.name)));
      setNewColleagueName("");
      setNewColleagueEmail("");
      toast.success("Collègue ajouté");
    } catch (err) {
      toast.error(err.message || "Erreur réseau");
    } finally {
      setAddingColleague(false);
    }
  };

  const handleDeleteColleague = async (colleagueId) => {
    try {
      const r = await apiFetch(`${API}/users/me/colleagues/${encodeURIComponent(colleagueId)}`, {
        method: "DELETE"
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        toast.error(d.detail || "Erreur lors de la suppression");
        return;
      }
      setColleagues((prev) => prev.filter((c) => c.colleague_id !== colleagueId));
      toast.success("Collègue supprimé");
    } catch (err) {
      toast.error(err.message || "Erreur réseau");
    }
  };

  // Check Outlook connection status
  const checkOutlookConnection = useCallback(async () => {
    try {
      // First check if OAuth is configured
      const statusRes = await fetch(`${API}/auth/microsoft/status`);
      const statusData = await statusRes.json();
      
      if (!statusData.configured) {
        setOutlookStatus({
          loading: false,
          configured: false,
          connected: false,
          email: null,
          displayName: null,
          tokenExpired: false
        });
        return;
      }

      // Check actual connection
      const response = await fetch(`${API}/auth/microsoft/connection`, { credentials: "include" });
      const connData = await connRes.json();
      
      setOutlookStatus({
        loading: false,
        configured: true,
        connected: connData.connected,
        email: connData.email || null,
        displayName: connData.display_name || null,
        tokenExpired: connData.token_expired || false
      });
    } catch (error) {
      console.error("Error checking Outlook status:", error);
      setOutlookStatus({
        loading: false,
        configured: false,
        connected: false,
        email: null,
        displayName: null,
        tokenExpired: false
      });
    }
  }, []);

  // Handle Outlook OAuth callback params
  useEffect(() => {
    const outlookSuccess = searchParams.get("outlook_success");
    const outlookError = searchParams.get("outlook_error");
    const outlookEmail = searchParams.get("email");

    if (outlookSuccess === "true") {
      toast.success("Compte Outlook connecté !", {
        description: outlookEmail ? `Connecté en tant que ${outlookEmail}` : "Votre boîte mail est maintenant synchronisée."
      });
      // Remove params from URL
      window.history.replaceState({}, "", window.location.pathname);
      checkOutlookConnection();
    } else if (outlookError) {
      const errorMessages = {
        "not_configured": "L'intégration Outlook n'est pas encore configurée.",
        "missing_code": "Erreur d'authentification. Veuillez réessayer.",
        "token_failed": "Impossible d'obtenir les tokens. Veuillez réessayer.",
        "no_token": "Erreur de connexion. Veuillez réessayer.",
        "callback_failed": "Erreur lors de la connexion. Veuillez réessayer."
      };
      toast.error("Erreur de connexion Outlook", {
        description: errorMessages[outlookError] || outlookError
      });
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [searchParams, checkOutlookConnection]);

  // Load Outlook status on mount
  useEffect(() => {
    checkOutlookConnection();
  }, [checkOutlookConnection]);

  const connectOutlook = async () => {
    setConnectingOutlook(true);
    try {
      const response = await fetch(`${API}/auth/microsoft/login?language=fr`, { credentials: "include" });
      const data = await response.json();
      
      if (data.auth_url) {
        window.location.href = data.auth_url;
      } else {
        toast.error("Impossible de démarrer la connexion Outlook");
        setConnectingOutlook(false);
      }
    } catch (error) {
      console.error("Error connecting Outlook:", error);
      toast.error("Erreur lors de la connexion Outlook");
      setConnectingOutlook(false);
    }
  };

  const disconnectOutlook = async () => {
    setDisconnectingOutlook(true);
    try {
      const response = await fetch(`${API}/auth/microsoft/disconnect`, {
        method: "DELETE",
        credentials: "include"
      });
      const data = await response.json();
      
      if (response.ok) {
        toast.success(data.message_fr || "Compte Outlook déconnecté");
        setOutlookStatus(prev => ({
          ...prev,
          connected: false,
          email: null,
          displayName: null
        }));
      } else {
        toast.error("Erreur lors de la déconnexion");
      }
    } catch (error) {
      console.error("Error disconnecting Outlook:", error);
      toast.error("Erreur lors de la déconnexion");
    } finally {
      setDisconnectingOutlook(false);
    }
  };

  const checkPaymentStatus = useCallback(async (sessionId) => {
    setCheckingPayment(true);
    let attempts = 0;
    const maxAttempts = 5;
    const pollInterval = 2000;

    const poll = async () => {
      if (attempts >= maxAttempts) {
        toast.error("Impossible de vérifier le paiement");
        setCheckingPayment(false);
        return;
      }

      try {
        const response = await fetch(`${API}/payments/status/${sessionId}`, {
          credentials: "include"
        });

        if (response.ok) {
          const data = await response.json();
          
          if (data.payment_status === "paid") {
            toast.success("Paiement réussi ! Votre abonnement est actif.");
            const userRes = await fetch(`${API}/auth/me`, { credentials: "include" });
            if (userRes.ok) {
              const userData = await userRes.json();
              setUser(userData);
            }
            setCheckingPayment(false);
            return;
          } else if (data.status === "expired") {
            toast.error("Session expirée");
            setCheckingPayment(false);
            return;
          }
        }

        attempts++;
        setTimeout(poll, pollInterval);
      } catch (error) {
        attempts++;
        setTimeout(poll, pollInterval);
      }
    };

    poll();
  }, [setUser]);

  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    if (sessionId) {
      checkPaymentStatus(sessionId);
    }
  }, [searchParams, checkPaymentStatus]);

  const saveProfile = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API}/settings/profile`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(profile)
      });

      if (response.ok) {
        const updated = await response.json();
        setUser({ ...user, ...updated });
        toast.success("Profil mis à jour");
      }
    } catch (error) {
      toast.error("Erreur lors de la sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  const handleUpgrade = async (planId) => {
    try {
      const originUrl = window.location.origin;
      const response = await fetch(`${API}/payments/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ plan_id: planId, origin_url: originUrl })
      });

      if (response.ok) {
        const data = await response.json();
        window.location.href = data.url;
      }
    } catch (error) {
      toast.error("Erreur de paiement");
    }
  };

  const getPlanInfo = (plan) => {
    switch (plan) {
      case "starter": return { name: "Starter", price: "39€/mois", color: "bg-slate-500" };
      case "pro": return { name: "Pro", price: "99€/mois", color: "bg-blue-500" };
      case "business": return { name: "Business", price: "250€/mois", color: "bg-purple-500" };
      default: return { name: "Essai gratuit", price: "0€", color: "bg-green-500" };
    }
  };

  const currentPlan = getPlanInfo(user?.subscription_plan);

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="settings-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900 dark:text-white" style={{ fontFamily: 'Outfit' }}>
          Paramètres
        </h1>
        <p className="text-slate-500 dark:text-slate-400 mt-1">
          Gérez votre compte et vos préférences.
        </p>
      </div>

      {/* Payment Status */}
      {checkingPayment && (
        <Card className="glass-card bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
          <CardContent className="p-5">
            <div className="flex items-center gap-4">
              <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
              <div>
                <h4 className="font-medium text-slate-900 dark:text-white">
                  Vérification du paiement...
                </h4>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  Veuillez patienter.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="profile" className="space-y-6">
        <TabsList className="bg-slate-100 dark:bg-slate-800">
          <TabsTrigger value="profile" className="flex items-center gap-2">
            <User className="w-4 h-4" />
            Profil
          </TabsTrigger>
          <TabsTrigger value="outlook" className="flex items-center gap-2">
            <Mail className="w-4 h-4" />
            Outlook
          </TabsTrigger>
          <TabsTrigger value="preferences" className="flex items-center gap-2">
            <Settings2 className="w-4 h-4" />
            Préférences
          </TabsTrigger>
          <TabsTrigger value="colleagues" className="flex items-center gap-2" data-testid="settings-tab-colleagues">
            <Users className="w-4 h-4" />
            Mes collègues
          </TabsTrigger>
          <TabsTrigger value="subscription" className="flex items-center gap-2">
            <CreditCard className="w-4 h-4" />
            Abonnement
          </TabsTrigger>
        </TabsList>

        {/* Profile Tab */}
        <TabsContent value="profile">
          <Card className="glass-card">
            <CardHeader>
              <CardTitle>Informations du profil</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="name">Nom complet</Label>
                  <Input
                    id="name"
                    value={profile.name}
                    onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    value={user?.email || ""}
                    disabled
                    className="bg-slate-50 dark:bg-slate-800"
                  />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="company">Entreprise</Label>
                  <Input
                    id="company"
                    value={profile.company_name}
                    onChange={(e) => setProfile({ ...profile, company_name: e.target.value })}
                  />
                </div>
              </div>

              <Separator />

              <div className="flex justify-end">
                <Button onClick={saveProfile} disabled={saving} className="bg-blue-600 hover:bg-blue-700">
                  {saving ? "Enregistrement..." : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Enregistrer
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Outlook Tab */}
        <TabsContent value="outlook">
          <Card className="glass-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="w-5 h-5 text-blue-500" />
                Connexion Microsoft Outlook
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {outlookStatus.loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
                  <span className="ml-2 text-slate-500">Vérification de la connexion...</span>
                </div>
              ) : !outlookStatus.configured ? (
                // OAuth not configured
                <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-6">
                  <div className="flex items-start gap-4">
                    <AlertCircle className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-yellow-900 dark:text-yellow-200 mb-1">
                        Intégration non configurée
                      </h4>
                      <p className="text-yellow-700 dark:text-yellow-300 text-sm">
                        L'intégration Microsoft Outlook nécessite une configuration par l'administrateur.
                        Contactez votre équipe technique pour activer cette fonctionnalité.
                      </p>
                    </div>
                  </div>
                </div>
              ) : outlookStatus.connected ? (
                // Connected state
                <div className="space-y-6">
                  <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-6">
                    <div className="flex items-start gap-4">
                      <CheckCircle2 className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <h4 className="font-medium text-green-900 dark:text-green-200 mb-1">
                          Compte Outlook connecté
                        </h4>
                        <p className="text-green-700 dark:text-green-300 text-sm mb-3">
                          Votre boîte mail est synchronisée avec ALIA.
                        </p>
                        <div className="bg-white dark:bg-slate-800 rounded-md p-3 border border-green-200 dark:border-green-700">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center text-white font-medium">
                              {outlookStatus.displayName?.charAt(0) || outlookStatus.email?.charAt(0) || "O"}
                            </div>
                            <div>
                              <p className="font-medium text-slate-900 dark:text-white">
                                {outlookStatus.displayName || "Compte Outlook"}
                              </p>
                              <p className="text-sm text-slate-500 dark:text-slate-400">
                                {outlookStatus.email}
                              </p>
                            </div>
                          </div>
                        </div>
                        {outlookStatus.tokenExpired && (
                          <p className="text-yellow-600 dark:text-yellow-400 text-sm mt-3 flex items-center gap-1">
                            <AlertCircle className="w-4 h-4" />
                            Session expirée. Reconnectez-vous pour actualiser.
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  <Separator />

                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium text-slate-900 dark:text-white">Déconnecter le compte</h4>
                      <p className="text-sm text-slate-500 dark:text-slate-400">
                        Cela supprimera l'accès d'ALIA à votre boîte mail.
                      </p>
                    </div>
                    <Button 
                      variant="outline" 
                      className="border-red-300 text-red-600 hover:bg-red-50 dark:border-red-700 dark:hover:bg-red-900/20"
                      onClick={disconnectOutlook}
                      disabled={disconnectingOutlook}
                      data-testid="disconnect-outlook-btn"
                    >
                      {disconnectingOutlook ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Unlink className="w-4 h-4 mr-2" />
                      )}
                      Déconnecter
                    </Button>
                  </div>
                </div>
              ) : (
                // Not connected state
                <div className="space-y-6">
                  <div className="bg-slate-50 dark:bg-slate-800/50 rounded-lg p-6 text-center">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                      <Mail className="w-8 h-8 text-blue-600" />
                    </div>
                    <h4 className="text-lg font-medium text-slate-900 dark:text-white mb-2">
                      Connectez votre compte Outlook
                    </h4>
                    <p className="text-slate-500 dark:text-slate-400 text-sm mb-6 max-w-md mx-auto">
                      Autorisez ALIA à accéder à votre boîte mail Microsoft Outlook pour bénéficier 
                      de l'intelligence email, des recommandations d'actions et du suivi automatisé.
                    </p>
                    <Button 
                      onClick={connectOutlook}
                      disabled={connectingOutlook}
                      className="bg-blue-600 hover:bg-blue-700"
                      data-testid="connect-outlook-btn"
                    >
                      {connectingOutlook ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Connexion en cours...
                        </>
                      ) : (
                        <>
                          <Link className="w-4 h-4 mr-2" />
                          Connecter Microsoft Outlook
                        </>
                      )}
                    </Button>
                  </div>

                  <Separator />

                  <div className="text-sm text-slate-500 dark:text-slate-400 space-y-2">
                    <h5 className="font-medium text-slate-700 dark:text-slate-300">Permissions requises :</h5>
                    <ul className="space-y-1 list-disc list-inside">
                      <li>Lecture de votre profil (nom, email)</li>
                      <li>Lecture de vos emails entrants</li>
                      <li>Aucune modification ni envoi de votre part</li>
                    </ul>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Preferences Tab */}
        <TabsContent value="preferences">
          <Card className="glass-card">
            <CardHeader>
              <CardTitle>Préférences IA</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label>Niveau d'automatisation</Label>
                  <Select 
                    value={profile.automation_level} 
                    onValueChange={(v) => setProfile({ ...profile, automation_level: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="suggestions">Suggestions uniquement</SelectItem>
                      <SelectItem value="semi_automatic">Semi-automatique</SelectItem>
                      <SelectItem value="automatic">Automatique</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Ton des réponses</Label>
                  <Select 
                    value={profile.tone_preference} 
                    onValueChange={(v) => setProfile({ ...profile, tone_preference: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="formal">Formel</SelectItem>
                      <SelectItem value="commercial">Commercial</SelectItem>
                      <SelectItem value="friendly">Cordial</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <Separator />

              <div className="flex justify-end">
                <Button onClick={saveProfile} disabled={saving} className="bg-blue-600 hover:bg-blue-700">
                  {saving ? "Enregistrement..." : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Enregistrer
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Colleagues Tab — for email delegation */}
        <TabsContent value="colleagues" data-testid="colleagues-tab">
          <Card className="glass-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5 text-purple-600" />
                Mes collègues
              </CardTitle>
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                Enregistrez vos collègues pour les sélectionner en un clic lors de la délégation d&apos;un e-mail.
              </p>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Add colleague form */}
              <form onSubmit={handleAddColleague} className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-3 items-end" data-testid="add-colleague-form">
                <div>
                  <Label htmlFor="colleague-name">Nom</Label>
                  <Input
                    id="colleague-name"
                    value={newColleagueName}
                    onChange={(e) => setNewColleagueName(e.target.value)}
                    placeholder="Marie Dupont"
                    data-testid="colleague-name-input"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="colleague-email">Adresse e-mail</Label>
                  <Input
                    id="colleague-email"
                    type="email"
                    value={newColleagueEmail}
                    onChange={(e) => setNewColleagueEmail(e.target.value)}
                    placeholder="marie@entreprise.com"
                    data-testid="colleague-email-input"
                    required
                  />
                </div>
                <Button
                  type="submit"
                  disabled={addingColleague}
                  className="bg-purple-600 hover:bg-purple-700 text-white h-10"
                  data-testid="add-colleague-btn"
                >
                  {addingColleague ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <UserPlus className="w-4 h-4 mr-2" />
                  )}
                  Ajouter
                </Button>
              </form>

              <Separator />

              {/* Colleagues list */}
              {colleaguesLoading ? (
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Chargement…
                </div>
              ) : colleagues.length === 0 ? (
                <p className="text-sm text-slate-500 dark:text-slate-400" data-testid="colleagues-empty">
                  Aucun collègue enregistré pour le moment.
                </p>
              ) : (
                <ul className="divide-y divide-slate-200 dark:divide-slate-700" data-testid="colleagues-list">
                  {colleagues.map((c) => (
                    <li key={c.colleague_id} className="flex items-center justify-between py-3">
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-slate-900 dark:text-white truncate">{c.name}</p>
                        <p className="text-sm text-slate-500 dark:text-slate-400 truncate">{c.email}</p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteColleague(c.colleague_id)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
                        data-testid={`delete-colleague-${c.colleague_id}`}
                        aria-label={`Supprimer ${c.name}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Subscription Tab - Enterprise Billing */}
        <TabsContent value="subscription" data-testid="subscription-tab">
          <SubscriptionTab />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SettingsPage;
